import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiMessageSquare,
  FiSend,
  FiCornerDownRight,
  FiEdit2,
  FiTrash2,
  FiMoreVertical,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { Dropdown, Modal } from "react-bootstrap";
import Avatar from "../../components/notes/Avatar";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import "./PropertyComment.css";

const CustomCommentToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="saas-comment-more-btn"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </button>
));

const PropertyComment = ({
  Id,
  ownerId,
  commentType,
  allComments,
  addComment,
  editComment,
  deleteComment,
}) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const currentUserId =
    loggedInUser?.user?.id || loggedInUser?.id || loggedInUser?.userDetails?.id;
  const isOwner = Boolean(ownerId && Number(currentUserId) === Number(ownerId));

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyText, setReplyText] = useState({});
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [updatedComment, setUpdatedComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const [isReplyDelete, setIsReplyDelete] = useState(false);

  const userId = currentUserId;
  const token = loggedInUser?.refreshToken || loggedInUser?.token || loggedInUser?.accessToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL || baseUrl || "";

  const fetchCommentsUrl = `${baseUrl}${allComments}/${userId}/${Id}`;
  const addCommentUrl = `${baseUrl}${addComment}`;
  const editCommentUrl = `${baseUrl}${editComment}`;

  // Current user initials
  const currentUserName =
    [loggedInUser?.user?.first_name, loggedInUser?.user?.last_name]
      .filter(Boolean)
      .join(" ") || loggedInUser?.user?.name || "You";
  const currentUserInitial = currentUserName.charAt(0).toUpperCase() || "U";

  // Filter logic for visibility
  const filterCommentsForUser = (allList) => {
    if (isOwner) {
      return allList;
    }

    return allList
      .map((comment) => {
        if (comment.user?.id === ownerId) {
          return { ...comment, children: [] };
        }
        if (comment.user?.id === currentUserId) {
          return comment;
        }
        return null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.post(
        fetchCommentsUrl,
        {
          page_number: 1,
          page_size: 200,
          order_by: "DESC",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (
        response.data?.statusType === "SUCCESS" ||
        Array.isArray(response.data[commentType]) ||
        Array.isArray(response.data.comments)
      ) {
        setComments(response.data[commentType] || response.data.comments || []);
      } else {
        throw new Error("Failed to fetch comments");
      }
    } catch (err) {
      setError(err.message || "Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    if (e) e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        comment: newComment,
        user: { id: userId },
        desired_request: { id: Id },
      };
      const response = await axios.post(addCommentUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data?.statusType === "SUCCESS" || response.status === 200) {
        setNewComment("");
        await fetchComments();
      } else {
        throw new Error("Failed to add comment");
      }
    } catch (err) {
      setError(err.message || "Unable to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!updatedComment.trim()) return;
    setSubmitting(true);
    try {
      const response = await axios.put(
        editCommentUrl,
        {
          id: commentId,
          comment: updatedComment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS" || response.status === 200) {
        setEditingComment(null);
        setUpdatedComment("");
        await fetchComments();
      } else {
        throw new Error("Failed to edit comment");
      }
    } catch (err) {
      setError(err.message || "Failed to update comment");
    } finally {
      setSubmitting(false);
    }
  };

  // Open modal for comment delete
  const confirmDeleteComment = (commentId) => {
    setCommentIdToDelete(commentId);
    setIsReplyDelete(false);
    setShowDeleteModal(true);
  };

  // Open modal for reply delete
  const confirmDeleteReply = (replyId) => {
    setCommentIdToDelete(replyId);
    setIsReplyDelete(true);
    setShowDeleteModal(true);
  };

  // Delete comment or reply after confirmation
  const handleDeleteConfirmed = async () => {
    if (!commentIdToDelete) return;
    try {
      const deleteUrl = `${baseUrl}${deleteComment}${commentIdToDelete}`;
      const response = await axios.delete(deleteUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data?.statusType === "SUCCESS" || response.status === 200) {
        await fetchComments();
      } else {
        throw new Error("Failed to delete comment");
      }
    } catch (err) {
      setError("Failed to delete comment!");
    } finally {
      setShowDeleteModal(false);
      setCommentIdToDelete(null);
      setIsReplyDelete(false);
    }
  };

  const handleAddReply = async (parentId) => {
    if (!replyText[parentId]?.trim()) return;
    setSubmitting(true);
    try {
      const response = await axios.post(
        addCommentUrl,
        {
          parent_id: parentId,
          comment: replyText[parentId],
          user: { id: userId },
          desired_request: { id: Id },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS" || response.status === 200) {
        setReplyText((prev) => ({ ...prev, [parentId]: "" }));
        setActiveReplyId(null);
        await fetchComments();
      } else {
        throw new Error("Failed to add reply");
      }
    } catch (err) {
      setError(err.message || "Failed to add reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReply = async (replyId) => {
    if (!updatedComment.trim()) return;
    setSubmitting(true);
    try {
      const response = await axios.put(
        editCommentUrl,
        {
          id: replyId,
          comment: updatedComment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data?.statusType === "SUCCESS" || response.status === 200) {
        setEditingComment(null);
        setUpdatedComment("");
        await fetchComments();
      } else {
        throw new Error("Failed to edit reply");
      }
    } catch (err) {
      setError("Failed to edit reply");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleComments = filterCommentsForUser(comments);

  const renderReplies = (replies, level = 1) => (
    <div className={`saas-reply-thread level-${level}`}>
      {replies.map((reply) => {
        const author = [reply.user?.first_name, reply.user?.last_name]
          .filter(Boolean)
          .join(" ") || reply.user?.name || "Team Member";
        const isAuthorMe = reply.user?.id === userId;

        return (
          <div key={reply.id} className="saas-comment-card saas-reply-card">
            <div className="saas-comment-card__header">
              <div className="saas-comment-card__author-wrap">
                <Avatar
                  icon={reply.user?.first_name || author}
                  src={reply.user?.photo ? `${assetUrl}${reply.user.photo}` : null}
                  size={32}
                />
                <div>
                  <span className="saas-comment-author">{author}</span>
                  <div className="saas-comment-time">
                    <FiClock size={11} />
                    <TimeAgo timestamp={reply.created_on} />
                  </div>
                </div>
              </div>

              {isAuthorMe && (
                <Dropdown align="end">
                  <Dropdown.Toggle
                    as={CustomCommentToggle}
                    id={`dropdown-reply-${reply.id}`}
                  >
                    <FiMoreVertical size={14} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="saas-comment-dropdown-menu">
                    <Dropdown.Item
                      onClick={() => {
                        setEditingComment(reply.id);
                        setUpdatedComment(reply.comment);
                      }}
                    >
                      <FiEdit2 size={13} className="me-2 text-primary" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => confirmDeleteReply(reply.id)}
                      className="text-danger"
                    >
                      <FiTrash2 size={13} className="me-2" /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>

            <div className="saas-comment-body">
              {editingComment === reply.id ? (
                <div className="saas-comment-edit-box">
                  <textarea
                    rows={2}
                    value={updatedComment}
                    onChange={(e) => setUpdatedComment(e.target.value)}
                    className="saas-comment-input"
                  />
                  <div className="saas-comment-edit-actions">
                    <button
                      type="button"
                      className="saas-btn-cancel"
                      onClick={() => setEditingComment(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="saas-btn-primary"
                      onClick={() => handleEditReply(editingComment)}
                      disabled={submitting}
                    >
                      {submitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="saas-comment-text">{reply.comment}</p>
              )}
            </div>

            {/* Render nested children if any */}
            {reply.children && reply.children.length > 0 && (
              <div className="saas-replies-wrap">
                {renderReplies(reply.children, level + 1)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="saas-comments-root">
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "16px", fontWeight: 700 }}>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "14px", color: "#4b5563" }}>
          Are you sure you want to delete this {isReplyDelete ? "reply" : "comment"}? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="saas-btn-cancel"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="saas-btn-danger"
            onClick={handleDeleteConfirmed}
          >
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      {/* Discussion Header */}
      <div className="saas-comments-header">
        <h3 className="saas-comments-title">
          Comments
        </h3>
        <span className="saas-comments-count">
          {visibleComments.length} {visibleComments.length === 1 ? "comment" : "comments"}
        </span>
      </div>

      {/* Comment Input Box */}
      {!isOwner ? (
        <form onSubmit={handleAddComment} className="saas-comment-form">
          <div className="saas-comment-input-row">
            <div className="saas-comment-user-avatar">
              {currentUserInitial}
            </div>
            <div className="saas-comment-input-wrap">
              <textarea
                rows={2}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Write a comment..."
                className="saas-comment-textarea"
              />
              <div className="saas-comment-form-actions">
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="saas-btn-primary saas-btn-submit"
                >
                  <span>{submitting ? "Adding..." : "Add"}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="saas-author-notice">
          <FiAlertCircle size={15} />
          <span>You are the post author. You can view and reply directly to partner inquiries below.</span>
        </div>
      )}

      {/* Comments List / States */}
      {loading ? (
        <div className="saas-comments-loading">
          <div className="saas-skeleton-item" />
          <div className="saas-skeleton-item" />
        </div>
      ) : error ? (
        <div className="saas-comments-error">
          <FiAlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : visibleComments.length === 0 ? (
        <div className="saas-comments-empty">
          <FiMessageSquare size={28} className="text-muted" />
          <h4>No comments yet</h4>
          <p>Discuss details, ask questions, or connect with the partner sharing this property.</p>
        </div>
      ) : (
        <div className="saas-comments-list">
          {visibleComments.map((comment) => {
            const author = [comment.user?.first_name, comment.user?.last_name]
              .filter(Boolean)
              .join(" ") || comment.user?.name || "Community Partner";
            const isAuthorMe = comment.user?.id === userId;

            return (
              <div key={comment.id} className="saas-comment-card">
                <div className="saas-comment-card__header">
                  <div className="saas-comment-card__author-wrap">
                    <Avatar
                      icon={comment.user?.first_name || author}
                      src={comment.user?.photo ? `${assetUrl}${comment.user.photo}` : null}
                      size={36}
                    />
                    <div>
                      <span className="saas-comment-author">{author}</span>
                      <div className="saas-comment-time">
                        <FiClock size={11} />
                        <TimeAgo timestamp={comment.created_on} />
                      </div>
                    </div>
                  </div>

                  {isAuthorMe && (
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as={CustomCommentToggle}
                        id={`dropdown-${comment.id}`}
                      >
                        <FiMoreVertical size={14} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="saas-comment-dropdown-menu">
                        <Dropdown.Item
                          onClick={() => {
                            setEditingComment(comment.id);
                            setUpdatedComment(comment.comment);
                          }}
                        >
                          <FiEdit2 size={13} className="me-2 text-primary" /> Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => confirmDeleteComment(comment.id)}
                          className="text-danger"
                        >
                          <FiTrash2 size={13} className="me-2" /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>

                <div className="saas-comment-body">
                  {editingComment === comment.id ? (
                    <div className="saas-comment-edit-box">
                      <textarea
                        rows={2}
                        value={updatedComment}
                        onChange={(e) => setUpdatedComment(e.target.value)}
                        className="saas-comment-input"
                      />
                      <div className="saas-comment-edit-actions">
                        <button
                          type="button"
                          className="saas-btn-cancel"
                          onClick={() => setEditingComment(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="saas-btn-primary"
                          onClick={() => handleEditComment(comment.id)}
                          disabled={submitting}
                        >
                          {submitting ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="saas-comment-text">{comment.comment}</p>
                  )}
                </div>

                {/* Reply Button for others */}
                {comment.user?.id !== userId && (
                  <div className="saas-comment-footer">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveReplyId(activeReplyId === comment.id ? null : comment.id)
                      }
                      className="saas-reply-btn"
                    >
                      <FiCornerDownRight size={13} />
                      <span>{activeReplyId === comment.id ? "Cancel Reply" : "Reply"}</span>
                    </button>
                  </div>
                )}

                {/* Reply Input Box */}
                {activeReplyId === comment.id && (
                  <div className="saas-reply-input-box">
                    <textarea
                      rows={2}
                      value={replyText[comment.id] || ""}
                      onChange={(e) =>
                        setReplyText({ ...replyText, [comment.id]: e.target.value })
                      }
                      placeholder="Write your reply..."
                      className="saas-comment-textarea"
                    />
                    <div className="saas-comment-form-actions">
                      <button
                        type="button"
                        className="saas-btn-cancel"
                        onClick={() => setActiveReplyId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAddReply(comment.id)}
                        disabled={submitting || !replyText[comment.id]?.trim()}
                        className="saas-btn-primary"
                      >
                        {submitting ? "Replying..." : "Post Reply"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Nested Replies */}
                {Array.isArray(comment.children) && comment.children.length > 0 && (
                  <div className="saas-replies-wrap">
                    {renderReplies(comment.children, 1)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PropertyComment;